import React from 'react';
import {Alert, Box, Grid, Typography} from '@mui/material';
import moment from 'moment';
import {isNumber} from 'lodash';
import {useSnackbar} from 'notistack';
import {Prompt} from '../../components/Prompt/Prompt';
import {Button} from '../../components/Button/Button';
import SendAtrQuestionnaireModal from '../../components/ContactActions/SendAtrQuestionnaireModal';
import {RiskNotesSelector} from './RiskNotesSelector';
import {ReviewDetailsFragment_contacts} from '../../schema-types/ReviewDetailsFragment';
import {RiskProfileFragment} from '../../schema-types/RiskProfileFragment';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {riskNotesFormType} from './riskNotesFields';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import {useRiskNotesMutations} from './riskNotesQueries';
import {useOnUpdate} from '../../hooks/useOnUpdate';
import {riskNotesFormToVariables} from './RiskNotesFormHelpers';
import {RichEditor} from '../../components/RichEditor/RichEditor';
import {themeColors} from '../../theme/Theme';
import {useRTQ} from '../../hooks/useRTQ';
import {useReviewContext} from '../../contexts/ReviewContext';
import {ReviewStatusOneviewEnum} from '../../schema-types/globalTypes';
import Loading from '../../components/Loading/Loading';
import {UPDATES} from '../../shared/UPDATES';
import validateUUID from '../../utils/validateUUID';

export const RiskNoteContainer: React.FC<{
  readOnly?: boolean;
  reviewId: string;
  contact: ReviewDetailsFragment_contacts;
  accountId: string;
  halfWidth?: boolean;
  riskProfile?: RiskProfileFragment;
}> = ({readOnly = false, reviewId, contact, accountId, riskProfile, halfWidth}) => {
  const [isSendAtrModalOpen, setSendAtrModalOpen] = React.useState(false);
  const openSendAtrModal = () => setSendAtrModalOpen(true);
  const closeSendAtrModal = () => setSendAtrModalOpen(false);

  return (
    <>
      <RiskNoteUIInner
        readOnly={readOnly}
        reviewId={reviewId}
        contact={contact}
        accountId={accountId}
        riskProfile={riskProfile}
        openSendAtrModal={openSendAtrModal}
        halfWidth={halfWidth}
      />
      <SendAtrQuestionnaireModal
        contact={contact}
        accountId={accountId}
        isOpen={isSendAtrModalOpen}
        close={closeSendAtrModal}
      />
    </>
  );
};

export const RiskNoteUIInner: React.FC<{
  readOnly?: boolean;
  reviewId: string;
  contact: ReviewDetailsFragment_contacts;
  accountId: string;
  openSendAtrModal: () => void;
  riskProfile?: RiskProfileFragment;
  halfWidth?: boolean;
}> = ({
  readOnly = false,
  reviewId,
  accountId,
  contact,
  openSendAtrModal,
  riskProfile,
  halfWidth,
}) => {
  const {values} = useAutoForm<riskNotesFormType>();

  const {upsertRiskProfile, upsertRiskProfileLoading} = useRiskNotesMutations();
  const [{value: declinedReasonInputValue}, _, {setValue: setDeclinedReason}] =
    useAutoField<string | undefined>(
      `outcomeRisk.${contact.id}.riskToleranceQuestionnaireDeclineReason`
    );
  const [{value: isRiskToleranceQuestionnaireDeclinedValue}] = useAutoField<
    boolean | undefined
  >(`outcomeRisk.${contact.id}.isRiskToleranceQuestionnaireDeclined`);

  const onDeclinedChanged = async () => {
    const variables = riskNotesFormToVariables([contact.id], values, reviewId);
    await upsertRiskProfile({
      variables,
    });
  };

  useOnUpdate(() => {
    onDeclinedChanged();
  }, [declinedReasonInputValue, isRiskToleranceQuestionnaireDeclinedValue]);
  const {atrName} = useRTQ();
  const initialRiskProfile =
    riskProfile && riskProfile.initialRiskProfile !== null
      ? riskProfile.initialRiskProfile
      : null;
  const initialRiskQuestionnaire =
    riskProfile && riskProfile.initialRiskToleranceQuestionnaire !== null
      ? riskProfile.initialRiskToleranceQuestionnaire
      : null;

  const {reviewDetails} = useReviewContext();

  if (!riskProfile) {
    return <Loading entityName="risk profile" />;
  }

  return (
    <Box
      data-testid="risk-profile"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 2,
        mb: 2,
        p: 2,
        flex: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid item>
          {initialRiskQuestionnaire?.assessedAt ? (
            <>
              {initialRiskQuestionnaire.isExpired && (
                <Box mb={1}>
                  <Alert severity="warning">Risk profile has expired</Alert>
                </Box>
              )}
              <Typography
                mr={1}
                variant="h5"
                color={
                  initialRiskQuestionnaire.isExpired
                    ? themeColors.red
                    : themeColors.backgroundHighlight
                }
              >
                {moment(initialRiskQuestionnaire?.assessedAt).format('DD/MM/YYYY')} -{' '}
                {moment(initialRiskQuestionnaire?.expiryDate).format('DD/MM/YYYY')}
              </Typography>
            </>
          ) : null}
          {/* {(riskProfile?.riskToleranceQuestionnaireDeclineReason?.length || 0) > 0 && (
            <Box mb={1} alignItems="center">
              <Typography mr={3} variant="h5">
                Risk Tolerance Questionnaire Decline Reason:
              </Typography>
              <Typography variant="body1">
                {riskProfile?.riskToleranceQuestionnaireDeclineReason
                  ? parseHTML(riskProfile?.riskToleranceQuestionnaireDeclineReason)
                  : ''}
              </Typography>
            </Box>
          )} */}
          {isNumber(initialRiskQuestionnaire?.score) ? (
            <Box mb={2}>
              <Typography mb={1} variant="h5">
                Risk Tolerance Questionnaire:
              </Typography>
              <Typography mb={1}>
                Result of the most recent {atrName} to the creation of this review.
              </Typography>
              <RiskNotesSelector
                currentScore={initialRiskQuestionnaire.score}
                readOnly
                size="small"
              />
            </Box>
          ) : null}
          {!initialRiskQuestionnaire?.score ? (
            <Box pb={initialRiskProfile?.score ? 2 : 0} display="flex">
              <Prompt
                variant="warning"
                title={`${contact.simplifiedName} does not have a valid Saltus risk tolerance questionnaire`}
                content={
                  <Box display="flex" flexDirection="column">
                    <Typography>
                      In order to proceed this review, they must either complete their
                      Risk Tolerance Questionnaire or be marked as declined risk tolerance
                      questionnaire.
                    </Typography>
                    <Box display="flex" gap={2} mt={2} mb={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        eventName="request-new-rtq"
                        onClick={openSendAtrModal}
                      >
                        Request a new Risk Tolerance Questionnaire
                      </Button>
                      {reviewDetails?.statusOneview !== ReviewStatusOneviewEnum.CLOSED &&
                        reviewDetails?.statusOneview !==
                          ReviewStatusOneviewEnum.COMPLETED && (
                          <>
                            <ResyncScoreButton contact={contact} reviewId={reviewId} />
                          </>
                        )}
                    </Box>
                    <AutoField
                      field={{
                        id: `outcomeRisk.${contact.id}.isRiskToleranceQuestionnaireDeclined`,
                        label: 'Client is not completing an RTQ',
                        type: ActionType.checkboxToggle,
                      }}
                    />
                    {isRiskToleranceQuestionnaireDeclinedValue && (
                      <RichEditor
                        formikId={`outcomeRisk.${contact.id}.riskToleranceQuestionnaireDeclineReason`}
                        placeholder="Reason client declined"
                        onSave={async (val) => {
                          setDeclinedReason(UPDATES._1011, val);
                        }}
                        charLimit={1000}
                        initialValue={declinedReasonInputValue}
                      />
                    )}
                  </Box>
                }
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                paddingTop: 1,
                paddingBottom: 2,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                eventName="send-new-questionnaire"
                onClick={openSendAtrModal}
              >
                Send a New Questionnaire
              </Button>
              {reviewDetails?.statusOneview !== ReviewStatusOneviewEnum.CLOSED &&
                reviewDetails?.statusOneview !== ReviewStatusOneviewEnum.COMPLETED && (
                  <ResyncScoreButton contact={contact} reviewId={reviewId} />
                )}
            </Box>
          )}
          {isNumber(initialRiskProfile?.score) ? (
            <Box mb={2}>
              <Typography mr={1} variant="h5">
                Risk Profile:
              </Typography>
              <Typography mb={2}>Risk Profile set on previous review.</Typography>
              <RiskNotesSelector
                currentScore={initialRiskProfile.score}
                readOnly
                size="small"
              />
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

const ResyncScoreButton: React.FC<{
  reviewId: string;
  contact: ReviewDetailsFragment_contacts;
}> = ({contact, reviewId}) => {
  const {
    resyncCuroAtrForRiskProfileMutation,
    resyncCuroAtrForRiskProfileLoading,
    resyncCuroAtrForRiskProfileError,
    refetchRiskData,
  } = useRiskNotesMutations();
  const {enqueueSnackbar} = useSnackbar();
  return (
    <Button
      variant="contained"
      color="secondary"
      eventName="send-new-questionnaire"
      onClick={() => {
        resyncCuroAtrForRiskProfileMutation({
          variables: {
            contactId: contact.id,
            reviewId: validateUUID(reviewId)!,
          },
        }).then(async ({data}) => {
          if (data?.resyncCuroAtrForRiskProfile?.ok) {
            enqueueSnackbar({
              message: 'Curo RTQ resync successful',
              variant: 'success',
            });
            await refetchRiskData();
            return;
          }

          if (data?.resyncCuroAtrForRiskProfile?.errors) {
            enqueueSnackbar({
              message: `Curo RTQ resync failed: ${data?.resyncCuroAtrForRiskProfile?.errors.join()}`,
              variant: 'error',
            });
          }
        });
      }}
    >
      Resync Score
    </Button>
  );
};
